import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Copy } from "lucide-react";

// Code block with copy button. Defined at module level so React doesn't recreate
// the component type on every parent render.
function CodeBlock({ code, lang }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked */ }
    };
    return (
        <div className="my-2 rounded-md border border-white/10 bg-black/40 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-400 bg-white/5">
                <span>{lang || "code"}</span>
                <button onClick={copy} className="flex items-center gap-1 hover:text-white transition" data-testid="claude-code-copy">
                    {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    {copied ? "kopiert" : "kopieren"}
                </button>
            </div>
            <pre className="px-3 py-2 overflow-x-auto text-[12.5px] leading-relaxed">
                <code className="font-mono text-neutral-100 whitespace-pre">{code}</code>
            </pre>
        </div>
    );
}

// Markdown→React mappings (defined at module scope, see react/no-unstable-nested-components)
const MD_COMPONENTS = {
    p: ({ children }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap">{children}</p>,
    h1: ({ children }) => <h1 className="font-display text-xl text-white mt-3 mb-2 tracking-tight">{children}</h1>,
    h2: ({ children }) => <h2 className="font-display text-lg text-white mt-3 mb-1.5 tracking-tight">{children}</h2>,
    h3: ({ children }) => <h3 className="font-semibold text-[15px] text-white mt-2.5 mb-1">{children}</h3>,
    h4: ({ children }) => <h4 className="font-semibold text-sm text-white mt-2 mb-1">{children}</h4>,
    strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
    em: ({ children }) => <em className="italic text-neutral-200">{children}</em>,
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noreferrer"
           className="text-brand-300 underline decoration-brand-400/40 underline-offset-2 hover:decoration-brand-400">
            {children}
        </a>
    ),
    ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2 marker:text-brand-300/70">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2 marker:text-brand-300/80 marker:font-semibold">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-brand-400/60 pl-3 my-2 italic text-neutral-300">
            {children}
        </blockquote>
    ),
    hr: () => <hr className="my-3 border-white/10" />,
    table: ({ children }) => (
        <div className="my-3 overflow-x-auto rounded-md border border-white/10">
            <table className="w-full text-[13px] border-collapse">{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="bg-white/5 text-white">{children}</thead>,
    tr: ({ children }) => <tr className="border-b border-white/5 last:border-0">{children}</tr>,
    th: ({ children }) => <th className="text-left px-3 py-2 font-semibold">{children}</th>,
    td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
    code: ({ inline, className, children }) => {
        const code = String(children).replace(/\n$/, "");
        if (inline) {
            return <code className="bg-white/10 text-brand-200 rounded px-1.5 py-0.5 text-[12.5px] font-mono">{children}</code>;
        }
        const lang = (className || "").replace("language-", "") || "";
        return <CodeBlock code={code} lang={lang} />;
    },
    pre: ({ children }) => <>{children}</>, // unwrap; the code component already styles the block
};

const MD_PLUGINS = [remarkGfm];

/**
 * Renders Claude's responses as polished Markdown:
 * - GFM tables, task lists, strikethrough
 * - Code blocks with copy button + monospace styling
 * - Headings, bold, italic, blockquotes, lists
 * - Tuned for the dark "claude-bubble-ai" surface.
 */
export default function ClaudeMarkdown({ content }) {
    if (!content) return null;
    return (
        <div className="claude-md text-[14px] leading-relaxed">
            <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD_COMPONENTS}>
                {content}
            </ReactMarkdown>
        </div>
    );
}
